import React, { useEffect, useMemo, useState } from "react";
import ApsModels from "../../../../models";
import ApsServices from "../../../../services";
import commonService from "../../../../services/CommonService";
import nfirsStore from "../../../../stores/NFIRSStore";
import toastStore from "../../../../stores/ToastStore";
import CommonSpinner from "../../../Common/CommonSpinner";
import ConfirmDialog from "../../../Common/ConfirmDialog";
import {
  INFIRSTabProps,
  useNfirsTabChangedOnClick,
  useSaveOnNext,
} from "../NFIRSEntry";
import { FgInput, FgUseForm, useFgModel } from "../../../Common/FormGroups";
import AdvancedDropdown from "../../../Common/AdvancedDropdown";
import { NfirsFetchStatus, useNfirsGetData } from "../../NFIRSHelper";

function NFIRSBasicActions(props: INFIRSTabProps<any>) {
  const saveAction = () => {
    //do model pre-processing here
    const req = ApsServices.http.nfirsBasicActionsService.update(model.new);
    return req;
  };
  const {
    model,
    getModel,
    setModel,
    progress,
    setProgress,
    initModels,
    hasChanges,
    saveData,
  } = useFgModel<ApsModels.INfirsBasicActionInputDto>({
    objectName: "Actions",
    nfirsSectionTab: "Basic/Actions",
    promise: saveAction,
    afterSave: (data) => {
      initModels({ ...model.new, concurrencyToken: data.concurrencyToken });
    },
  });

  const onModelChange = (newModel: ApsModels.INfirsBasicActionInputDto) => {
    setModel({ ...model, new: { ...newModel } });
  };

  const [actionsTaken, setActionsTaken] = useState<number[]>([]);

  const getData = async () => {
    setProgress({ loading: true, errorCode: 0 });
    await ApsServices.http.nfirsBasicActionsService
      .get(props.id)
      .then((data) => {
        const mdl = {
          ...model.new,
          ...data,
          reportId: Number(props.id),
          nerisActionTypeIds: (data.nerisActionTypes || []).map((x) => x.id),
        };

        initModels(mdl);
        setValuesFromModel(mdl);
        setProgress({ loading: false });
        props.onChange(mdl);

        const tmp = [];
        if (data.nfirsActionTaken1Id) {
          tmp.push(data.nfirsActionTaken1Id);
        }
        if (data.nfirsActionTaken2Id) {
          tmp.push(data.nfirsActionTaken2Id);
        }
        if (data.nfirsActionTaken3Id) {
          tmp.push(data.nfirsActionTaken3Id);
        }
        if (tmp.length > 0) {
          setActionsTaken(tmp);
        }
      })
      .catch((err) => {
        toastStore.showError("Failed Getting Actions", err);
        setProgress({ loading: false });
      });
  };

  const submit = async (form: any) => {
    if (!hasChanges()) {
      if (!tabChanged.isSaveOnTabChanged) {
        nfirsStore.setMoveToNextTab();
      }
      tabChanged.setIsSaveOnTabChanged(false);
      return;
    }
    saveData(tabChanged.isSaveOnTabChanged);
  };

  const [cntLock, setCntLock] = useState(0);
  const tabChanged = useNfirsTabChangedOnClick("Basic", "Actions", () => {
    triggerSubmitForm();
  });
  useSaveOnNext("Basic", "Actions", (lock) => {
    setCntLock(lock ? cntLock + 1 : 0);
    if (lock) {
      return;
    }

    if (!hasChanges()) {
      nfirsStore.setMoveToNextTab();
      return;
    }

    triggerSubmitForm();
    if (!formState.isValid) {
      nfirsStore.resetMoveTabSection();
    }
  });

  useEffect(() => {
    if (cntLock) {
      triggerSubmitForm();
      setTimeout(() => {
        if (!formState.isValid) {
          nfirsStore.resetMoveTabSection();
        }
      }, 200);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cntLock]);

  useEffect(() => {
    if (!progress.loading && model?.new?.id !== null) {
      const mdl = JSON.stringify(model);
      if (hasChanges()) {
        props.onChange(mdl, true);
      } else {
        props.onChange(mdl);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [model]);

  const {
    registry,
    handleSubmit,
    formState,
    setValuesFromModel,
    formRef,
    triggerSubmitForm,
  } = FgUseForm({
    actionsTaken: {
      displayName: "actionsTaken",
      validation: {
        required: false,
        validate: {
          required: (val: any) => {
            if (props.header?.isNeris) {
              return true; //do nothing if NERIS
            }
            if (cntLock) {
              return actionsTaken.length > 0;
            }
            return true;
          },
        },
      },
    },
    suppressionPersonnel: {
      displayName: "Personnel",
      validation: {
        required: false,
        max: 9999,
        validate: {
          required: (val: any) => {
            if (cntLock) {
              return !commonService.isNullOrEmpty(val);
            }
            return true;
          },
          numberOnly: commonService.validations.numberOnly,
        },
      },
    },
    suppressionApparatus: {
      displayName: "Apparatus",
      validation: {
        required: false,
        max: 9999,
        validate: {
          required: (val: any) => {
            if (cntLock) {
              return !commonService.isNullOrEmpty(val);
            }
            return true;
          },
          numberOnly: commonService.validations.numberOnly,
        },
      },
    },
    emsPersonnel: {
      displayName: "Personnel",
      validation: {
        required: false,
        max: 9999,
        validate: {
          required: (val: any) => {
            if (cntLock) {
              return !commonService.isNullOrEmpty(val);
            }
            return true;
          },
          numberOnly: commonService.validations.numberOnly,
        },
      },
    },
    emsApparatus: {
      displayName: "Apparatus",
      validation: {
        required: false,
        max: 9999,
        validate: {
          required: (val: any) => {
            if (cntLock) {
              return !commonService.isNullOrEmpty(val);
            }
            return true;
          },
          numberOnly: commonService.validations.numberOnly,
        },
      },
    },
    otherPersonnel: {
      displayName: "Personnel",
      validation: {
        required: false,
        max: 9999,
        validate: {
          required: (val: any) => {
            if (cntLock) {
              return !commonService.isNullOrEmpty(val);
            }
            return true;
          },
          numberOnly: commonService.validations.numberOnly,
        },
      },
    },
    otherApparatus: {
      displayName: "Apparatus",
      validation: {
        required: false,
        max: 9999,
        validate: {
          required: (val: any) => {
            if (cntLock) {
              return !commonService.isNullOrEmpty(val);
            }
            return true;
          },
          numberOnly: commonService.validations.numberOnly,
        },
      },
    },
    fireServiceDeaths: {
      displayName: "Deaths",
      validation: {
        required: false,
        max: 999,
        validate: {
          required: (val: any) => {
            if (cntLock) {
              return !commonService.isNullOrEmpty(val);
            }
            return true;
          },
          numberOnly: commonService.validations.numberOnly,
        },
      },
    },
    fireServiceInjuries: {
      displayName: "Injuries",
      validation: {
        required: false,
        max: 999,
        validate: {
          required: (val: any) => {
            if (cntLock) {
              return !commonService.isNullOrEmpty(val);
            }
            return true;
          },
          numberOnly: commonService.validations.numberOnly,
        },
      },
    },
    civilianDeaths: {
      displayName: "Deaths",
      validation: {
        required: false,
        max: 999,
        validate: {
          required: (val: any) => {
            if (cntLock) {
              return !commonService.isNullOrEmpty(val);
            }
            return true;
          },
          numberOnly: commonService.validations.numberOnly,
        },
      },
    },
    civilianInjuries: {
      displayName: "Injuries",
      validation: {
        required: false,
        max: 999,
        validate: {
          required: (val: any) => {
            if (cntLock) {
              return !commonService.isNullOrEmpty(val);
            }
            return true;
          },
          numberOnly: commonService.validations.numberOnly,
        },
      },
    },
  });

  const actions = useNfirsGetData<
    ApsModels.INfirsActionTakenFavoriteOutputDto[]
  >(
    () => ApsServices.http.nfirsFavorite.getActionsTaken(false),
    "Actions Taken"
  );

  const nerisActions = useNfirsGetData(
    () => ApsServices.http.nfirsFavorite.getNerisActionTypeFavorites(false),
    "Actions Taken"
  );

  useEffect(() => {
    if (props.header?.isNeris) {
      nerisActions.getData();
    } else {
      actions.getData();
    }
    setProgress({ loading: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.header?.isNeris]);

  useEffect(() => {
    if (
      !progress.errorCode &&
      (actions.status === NfirsFetchStatus.Complete ||
        nerisActions.status === NfirsFetchStatus.Complete)
    ) {
      getData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [progress.errorCode, actions.status, nerisActions.status]);

  useEffect(() => {
    const copy = { ...model.new, actionsTaken: null };
    copy.nfirsActionTaken1Id = actionsTaken[0] || (null as any);
    copy.nfirsActionTaken2Id = actionsTaken[1] || (null as any);
    copy.nfirsActionTaken3Id = actionsTaken[2] || (null as any);
    onModelChange(copy);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actionsTaken]);

  const neriActionGroups = useMemo(() => {
    const list = nerisActions?.data || [];
    const groups: { id: any; label: string }[] = [];
    const subGroups: { id: any; label: string; groupId: any }[] = [];

    list
      .filter((o, i, list) => {
        return (
          i === list.findIndex((x) => x.actionGroupLabel === o.actionGroupLabel)
        );
      })
      .forEach((item) => {
        groups.push({
          id: item.actionGroupLabel,
          label: item.actionGroupLabel,
        });
      });

    list
      .filter((o, i, list) => {
        return (
          !commonService.isNullOrEmpty(o.actionTypeLabel) &&
          i ===
            list.findIndex(
              (x) =>
                x.actionGroupLabel === o.actionGroupLabel &&
                x.actionTypeLabel === o.actionTypeLabel
            )
        );
      })
      .forEach((item) => {
        subGroups.push({
          id: item.actionTypeLabel,
          label: item.actionTypeLabel,
          groupId: item.actionGroupLabel,
        });
      });

    groups.forEach((grp) => {
      const _subGroups = subGroups.filter((g) => g.groupId === grp.label);
      if (_subGroups.length === 0) {
        subGroups.push({
          groupId: grp.id,
          id: grp.id,
          label: grp.label,
        });
      }
    });

    return {
      groups,
      subGroups,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nerisActions.data]);

  return (
    <>
      <ConfirmDialog
        show={!!progress.errorCode}
        buttons="okonly"
        title={commonService.getConcurrencyTitle(progress.errorCode)}
        message={commonService.getConcurrencyMessage(progress.errorCode)}
        done={(rtn) => {
          if (progress.errorCode === 409) {
            setProgress({ errorCode: 0 });
          }
        }}
      />

      {progress.loading && (
        <div className="row">
          <div className="col-sm-12 mb-2">
            <CommonSpinner />
          </div>
        </div>
      )}

      {!progress.loading && (
        <form className="row" onSubmit={handleSubmit(submit)} ref={formRef}>
          <div className="col-sm-12 col-lg-6">
            <div className="row">
              <div className="col-sm-12 mb-2">
                <strong>Actions</strong>
              </div>
            </div>

            {!props.header?.isNeris && (
              <div className="mt-2 mb-4" key={`${props.header?.isNeris}`}>
                <label>
                  <span className="required-label">*</span>Actions Taken
                </label>
                <div className="actionTakenList">
                  {actionsTaken.map((action) => (
                    <div key={action}>
                      <div>
                        {
                          actions.data?.find((a) => action === a.id)
                            ?.description
                        }{" "}
                        (
                        {actions.data?.find((a) => action === a.id)?.externalId}
                        )
                      </div>
                      <div>
                        <i
                          className="fa fa-times pointer"
                          onClick={() => {
                            const list =
                              commonService.deepCloneJsonArray(actionsTaken);
                            const idx = list.indexOf(action);
                            list.splice(idx, 1);
                            setActionsTaken(list);
                          }}
                        ></i>
                      </div>
                    </div>
                  ))}
                </div>

                <div
                  className={`${
                    actionsTaken?.length < 3 ? "" : "display-none"
                  }`}
                >
                  <AdvancedDropdown
                    domId="actionsTaken"
                    value={null}
                    searchPlaceholder="Search and Add More Actions"
                    favoritesHandler={{
                      add: ApsServices.http.nfirsFavorite
                        .addFavoriteActionTaken,
                      remove:
                        ApsServices.http.nfirsFavorite
                          .removeFavoriteActionTaken,
                      afterUpdate: (list: any[]) => actions.setData(list),
                    }}
                    disabledOptionValues={[
                      getModel?.new?.nfirsActionTaken1Id,
                      getModel?.new?.nfirsActionTaken2Id,
                      getModel?.new?.nfirsActionTaken3Id,
                    ]}
                    options={(actions?.data || [])
                      .sort(commonService.sortByStringProperty("description"))
                      .map((o) => {
                        return {
                          label: `${o.description} (${o.externalId}) `,
                          value: o.id,
                          originalObject: o,
                          isFavorite: o.isFavorite,
                          groupId: 0,
                          subGroupId: 0,
                        };
                      })}
                    onChange={(data) => {
                      onModelChange({
                        ...model.new,
                        actionsTaken: Number(data) || (null as any),
                      } as any);
                      if (data) {
                        setActionsTaken([...actionsTaken, Number(data)]);
                      }
                    }}
                  ></AdvancedDropdown>
                </div>
              </div>
            )}

            {props.header?.isNeris && (
              <div className="mt-2 mb-4" key={`${props.header?.isNeris}`}>
                <div className="flex flex-row">
                  <label className="required-label flex-1">
                    NERIS Actions Tactics
                  </label>
                  <span>
                    <a
                      href="https://www.responserack.com/neris/nfirs-to-neris-actions-taken-tactics/"
                      target="_blank"
                    >
                      <i className="fa fa-link me-2"></i>NERIS Actions Tactics
                    </a>
                  </span>
                </div>
                <div className="actionTakenList">
                  {model.new.nerisActionTypeIds?.map((action) => (
                    <div key={action}>
                      <div>
                        {nerisActions.data
                          ?.filter((a) => action === a.id)
                          ?.map((a) => {
                            return [
                              a.actionGroupLabel,
                              a.actionTypeLabel,
                              a.actionSubTypeLabel,
                            ]
                              .filter((x) => !commonService.isNullOrEmpty(x))
                              .join(" | ");
                          })}
                      </div>
                      <div>
                        <i
                          className="fa fa-times pointer"
                          onClick={() => {
                            const list = commonService.deepCloneJsonArray(
                              model.new.nerisActionTypeIds
                            );
                            const idx = list.indexOf(action);
                            list.splice(idx, 1);
                            setModel((prev) => {
                              return {
                                ...prev,
                                new: {
                                  ...prev.new,
                                  nerisActionTypeIds: list,
                                },
                              };
                            });
                          }}
                        ></i>
                      </div>
                    </div>
                  ))}
                </div>

                <div>
                  <AdvancedDropdown
                    domId="nerisActionsTaken"
                    value={null}
                    searchPlaceholder="Search and Add More Actions"
                    favoritesHandler={{
                      add: ApsServices.http.nfirsFavorite
                        .addFavoriteNerisActionType,
                      remove:
                        ApsServices.http.nfirsFavorite.removeNerisActionType,
                      afterUpdate: (list: any[]) => nerisActions.setData(list),
                    }}
                    disabledOptionValues={[
                      ...(getModel?.new?.nerisActionTypeIds || []),
                    ]}
                    groups={neriActionGroups.groups
                      .sort(commonService.sortByStringProperty("label"))
                      .map((o) => {
                        return {
                          groupId: o.id,
                          groupLabel: o.label,
                        };
                      })}
                    subGroups={neriActionGroups.subGroups
                      .sort(commonService.sortByStringProperty("label"))
                      .map((o) => {
                        return {
                          groupId: o.groupId,
                          subGroupId: o.id,
                          subGroupLabel: o.label,
                        };
                      })}
                    options={(nerisActions?.data || [])
                      .map((o) => {
                        return {
                          label: [
                            o.actionGroupLabel,
                            o.actionTypeLabel,
                            o.actionSubTypeLabel,
                          ]
                            .filter((x) => !commonService.isNullOrEmpty(x))
                            .join(" | "),
                          value: o.id,
                          originalObject: o,
                          isFavorite: o.isFavorite,
                          groupId: o.actionGroupLabel,
                          subGroupId: commonService.isNullOrWhitespace(
                            o.actionTypeLabel
                          )
                            ? o.actionGroupLabel
                            : o.actionTypeLabel,
                        };
                      })
                      .sort(commonService.sortByStringProperty("label"))}
                    onChange={(data) => {
                      const actions = model.new.nerisActionTypeIds || [];
                      const newId = Number(data) || (null as any);
                      onModelChange({
                        ...model.new,
                        nerisActionTypeIds: [...actions, newId],
                      });
                    }}
                  ></AdvancedDropdown>
                </div>
              </div>
            )}

            <div className="row">
              <div className="col-sm-12 mb-2">
                <strong>Resources</strong>
              </div>

              <div className="col-sm-12 mt-2">
                <label>
                  <span className="required-label">*</span>Suppression
                </label>
                <div className="row">
                  <div className="col-sm-6">
                    <FgInput
                      id="suppressionPersonnel"
                      placeHolder="Personnel"
                      label="Personnel"
                      registeredField={registry.suppressionPersonnel}
                      formState={formState}
                      onChange={(data) =>
                        onModelChange({
                          ...model.new,
                          suppressionPersonnel: Number(data) || 0,
                        } as any)
                      }
                      type="number"
                    ></FgInput>
                  </div>
                  <div className="col-sm-6">
                    <FgInput
                      id="suppressionApparatus"
                      placeHolder="Apparatus"
                      label="Apparatus"
                      registeredField={registry.suppressionApparatus}
                      formState={formState}
                      onChange={(data) =>
                        onModelChange({
                          ...model.new,
                          suppressionApparatus: Number(data) || 0,
                        } as any)
                      }
                      type="number"
                    ></FgInput>
                  </div>
                </div>

                <label>
                  <span className="required-label">*</span>EMS
                </label>
                <div className="row">
                  <div className="col-sm-6">
                    <FgInput
                      id="emsPersonnel"
                      placeHolder="Personnel"
                      label="Personnel"
                      registeredField={registry.emsPersonnel}
                      formState={formState}
                      onChange={(data) =>
                        onModelChange({
                          ...model.new,
                          emsPersonnel: Number(data) || 0,
                        } as any)
                      }
                      type="number"
                    ></FgInput>
                  </div>
                  <div className="col-sm-6">
                    <FgInput
                      id="emsApparatus"
                      placeHolder="Apparatus"
                      label="Apparatus"
                      registeredField={registry.emsApparatus}
                      formState={formState}
                      onChange={(data) =>
                        onModelChange({
                          ...model.new,
                          emsApparatus: Number(data) || 0,
                        } as any)
                      }
                      type="number"
                    ></FgInput>
                  </div>
                </div>

                <label>
                  <span className="required-label">*</span>Other
                </label>
                <div className="row">
                  <div className="col-sm-6">
                    <FgInput
                      id="otherPersonnel"
                      placeHolder="Personnel"
                      label="Personnel"
                      registeredField={registry.otherPersonnel}
                      formState={formState}
                      onChange={(data) =>
                        onModelChange({
                          ...model.new,
                          otherPersonnel: Number(data) || 0,
                        } as any)
                      }
                      type="number"
                    ></FgInput>
                  </div>
                  <div className="col-sm-6">
                    <FgInput
                      id="otherApparatus"
                      placeHolder="Apparatus"
                      label="Apparatus"
                      registeredField={registry.otherApparatus}
                      formState={formState}
                      onChange={(data) =>
                        onModelChange({
                          ...model.new,
                          otherApparatus: Number(data) || 0,
                        } as any)
                      }
                      type="number"
                    ></FgInput>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="col-sm-12 col-lg-6">
            <div className="row">
              <div className="col-sm-12 mb-2">
                <strong>Casualties</strong>
              </div>

              <div className="col-sm-12 mt-2">
                <label>
                  <span className="required-label">*</span>Fire Service
                </label>
                <div className="row">
                  <div className="col-sm-6">
                    <FgInput
                      id="fireServiceDeaths"
                      placeHolder="Deaths"
                      label="Deaths"
                      registeredField={registry.fireServiceDeaths}
                      formState={formState}
                      onChange={(data) =>
                        onModelChange({
                          ...model.new,
                          fireServiceDeaths: Number(data) || 0,
                        } as any)
                      }
                      type="number"
                    ></FgInput>
                  </div>
                  <div className="col-sm-6">
                    <FgInput
                      id="fireServiceInjuries"
                      placeHolder="Injuries"
                      label="Injuries"
                      registeredField={registry.fireServiceInjuries}
                      formState={formState}
                      onChange={(data) =>
                        onModelChange({
                          ...model.new,
                          fireServiceInjuries: Number(data) || 0,
                        } as any)
                      }
                      type="number"
                    ></FgInput>
                  </div>
                </div>

                <label>
                  <span className="required-label">*</span>Civilian
                </label>
                <div className="row">
                  <div className="col-sm-6">
                    <FgInput
                      id="civilianDeaths"
                      placeHolder="Deaths"
                      label="Deaths"
                      registeredField={registry.civilianDeaths}
                      formState={formState}
                      onChange={(data) =>
                        onModelChange({
                          ...model.new,
                          civilianDeaths: Number(data) || 0,
                        } as any)
                      }
                      type="number"
                    ></FgInput>
                  </div>
                  <div className="col-sm-6">
                    <FgInput
                      id="civilianInjuries"
                      placeHolder="Injuries"
                      label="Injuries"
                      registeredField={registry.civilianInjuries}
                      formState={formState}
                      onChange={(data) =>
                        onModelChange({
                          ...model.new,
                          civilianInjuries: Number(data) || 0,
                        } as any)
                      }
                      type="number"
                    ></FgInput>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <button type="submit" hidden></button>
        </form>
      )}
    </>
  );
}

export default NFIRSBasicActions;
